/* drawlz-bis.js — Back-in-stock ("Notify Me") capture, sitewide.
 *
 * WHY THIS IS A SEPARATE FILE: assets/drawlz-pdp.js is 74 KB, far past the
 * ~22 KB whole-file push ceiling this project works under, so it cannot be
 * safely re-emitted. Everything here layers on top of it instead, using two
 * hooks that already exist in that file:
 *
 *   1. its size-pill handler returns early on .is-sold-out pills, so our
 *      handler for those pills can never race it.
 *   2. its CTA writer returns early when the button carries .is-notify, so
 *      adding that class hands us the button and drawlz-pdp.js stops touching
 *      the label. Removing it hands the button straight back.
 *
 * Klaviyo: the public company id is READ FROM THE PAGE (the onsite klaviyo.js
 * tag already embedded by theme.liquid) rather than hardcoded, so there is
 * exactly one source of truth. Subscriptions go through Klaviyo's official
 * client Back-in-Stock endpoint.
 *
 * Email only by design. Phone/SMS is deliberately NOT collected: SMS consent
 * is a separate affirmative opt-in under TCPA and must not be inferred, and
 * SMS enablement on the account was not verified. Add it behind a confirmed
 * flag, never bundled into the marketing checkbox.
 */
(function () {
  'use strict';

  var REVISION = '2024-10-15';
  var BIS_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var SUB_URL = 'https://a.klaviyo.com/client/subscriptions/';

  /* ---- Klaviyo config, discovered not hardcoded ---------------------- */
  function klaviyoCompanyId() {
    var s = document.querySelector('script[src*="klaviyo.js"][src*="company_id="]');
    if (s) {
      var m = s.src.match(/company_id=([A-Za-z0-9]+)/);
      if (m) return m[1];
    }
    var any = document.querySelector('script[src*="/onsite/js/"]');
    if (any) {
      var m2 = any.src.match(/\/onsite\/js\/([A-Za-z0-9]{4,10})\//);
      if (m2) return m2[1];
    }
    return null;
  }
  /* the newsletter list already wired in theme.liquid, for the opt-in box */
  function klaviyoListId() {
    var inline = document.body ? document.body.innerHTML : '';
    var m = inline.match(/list:\s*['"]([A-Za-z0-9]{4,10})['"]/);
    return m ? m[1] : null;
  }

  var COMPANY = null, LIST = null;

  /* ---- small helpers ------------------------------------------------- */
  function esc(s) { return String(s == null ? '' : s); }
  function isOOS(pill) { return pill.classList.contains('is-sold-out'); }

  /* Every surface (hero, second close, both sticky bars, quick view) renders
     its own .drawlz-size__row. Scoping to the clicked pill's own row is what
     makes this work identically in the PDP and inside the quick-view modal. */
  function rowOf(pill) { return pill.closest('.drawlz-size__row') || document; }

  function oosSizesIn(row) {
    return Array.prototype.slice
      .call(row.querySelectorAll('[data-drawlz-size]'))
      .filter(isOOS)
      .map(function (p) {
        return { size: p.getAttribute('data-size'), variantId: variantIdFor(p) };
      })
      .filter(function (o) { return o.variantId; });
  }

  /* Single-option products carry the id on the pill. Multi-option products
     leave it blank on purpose (drawlz-pdp.js resolves colour+size together),
     so fall back to the variant map emitted next to the pills. */
  function variantIdFor(pill) {
    var direct = pill.getAttribute('data-variant-id');
    if (direct) return direct;

    var mapEl = document.querySelector('[data-drawlz-variant-map]');
    if (!mapEl) return null;
    var map;
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return null; }

    var size = pill.getAttribute('data-size');
    var colorSel = document.querySelector('[data-drawlz-color][aria-pressed="true"], [data-drawlz-swatch][aria-pressed="true"]');
    var color = colorSel ? (colorSel.getAttribute('data-color') || colorSel.getAttribute('data-value')) : null;

    var hit = null;
    (map.variants || []).forEach(function (v) {
      if (v.opts[map.sizeIndex] !== size) return;
      if (map.colorIndex >= 0 && color && v.opts[map.colorIndex] !== color) return;
      if (!hit) hit = v;
    });
    return hit ? String(hit.id) : null;
  }

  function productTitle(pill) {
    var qv = pill.closest('[data-dzc-qvm], .dzc-qvm, .drawlz-modal');
    var t = qv ? qv.querySelector('h2, h3, [data-qvm-title]') : document.querySelector('h1');
    return t ? t.textContent.trim() : '';
  }

  /* ---- CTA state ------------------------------------------------------
     Adding .is-notify is what makes drawlz-pdp.js let go of the button. We
     stash the original label so removing it restores the exact prior state. */
  function ctas() {
    return Array.prototype.slice.call(
      document.querySelectorAll('[data-drawlz-atc], [data-drawlz-notify]')
    );
  }
  function toNotify(size, variantId) {
    ctas().forEach(function (b) {
      if (!b.hasAttribute('data-bis-prev-label')) {
        b.setAttribute('data-bis-prev-label', b.textContent.trim());
      }
      b.classList.add('is-notify');
      b.setAttribute('data-drawlz-notify', '');
      b.setAttribute('data-bis-size', esc(size));
      if (variantId) b.setAttribute('data-bis-variant', variantId);
      b.removeAttribute('aria-disabled');
      b.textContent = b.getAttribute('data-label-notify') || 'Notify Me';
    });
  }
  function fromNotify() {
    ctas().forEach(function (b) {
      /* a product that is sold out at the SERVER stays a notify button — it was
         rendered that way by drawlz-cta-button and has no add-to-cart to go back to */
      if (!b.hasAttribute('data-drawlz-atc')) return;
      b.classList.remove('is-notify');
      b.removeAttribute('data-drawlz-notify');
      b.removeAttribute('data-bis-size');
      b.removeAttribute('data-bis-variant');
      var prev = b.getAttribute('data-bis-prev-label');
      if (prev) b.textContent = prev;
    });
  }

  /* ---- the drawer ----------------------------------------------------- */
  var el = null, lastFocus = null, state = { size: null, variantId: null, sizes: [] };

  function build() {
    if (el) return el;
    el = document.createElement('div');
    el.className = 'dzbis';
    el.setAttribute('hidden', '');
    el.innerHTML =
      '<div class="dzbis__scrim" data-bis-close></div>' +
      '<div class="dzbis__panel" role="dialog" aria-modal="true" aria-labelledby="dzbis-h">' +
        '<button type="button" class="dzbis__x" data-bis-close aria-label="Close">&times;</button>' +
        '<h2 class="dzbis__h" id="dzbis-h">Back in stock alerts<span class="dzbis__dot">.</span></h2>' +
        '<p class="dzbis__sub">Select your size and drop your email — we’ll hit you the second it’s back.</p>' +
        '<div class="dzbis__sizes" role="group" aria-label="Select a size"></div>' +
        '<form class="dzbis__form" novalidate>' +
          '<label class="dzbis__label" for="dzbis-email">Email</label>' +
          '<input class="dzbis__input" id="dzbis-email" type="email" name="email" required ' +
            'autocomplete="email" inputmode="email" placeholder="you@example.com">' +
          '<label class="dzbis__opt"><input type="checkbox" id="dzbis-mkt"> ' +
            '<span>Also send me new drops &amp; early access</span></label>' +
          '<button type="submit" class="dzbis__submit">Notify Me</button>' +
          '<p class="dzbis__msg" role="status" aria-live="polite"></p>' +
        '</form>' +
      '</div>';
    document.body.appendChild(el);

    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-bis-close]')) { close(); return; }
      var p = e.target.closest('[data-bis-size]');
      if (p) { pick(p.getAttribute('data-bis-size')); }
    });
    el.querySelector('.dzbis__form').addEventListener('submit', submit);
    return el;
  }

  function renderSizes() {
    var wrap = el.querySelector('.dzbis__sizes');
    wrap.innerHTML = state.sizes.map(function (s) {
      var on = s.size === state.size;
      return '<button type="button" class="dzbis__size' + (on ? ' is-on' : '') +
        '" data-bis-size="' + esc(s.size).replace(/"/g, '&quot;') + '" aria-pressed="' + on + '">' +
        esc(s.size) + '</button>';
    }).join('');
  }
  function pick(size) {
    var hit = state.sizes.filter(function (s) { return s.size === size; })[0];
    if (!hit) return;
    state.size = hit.size;
    state.variantId = hit.variantId;
    renderSizes();
  }

  function open(pill) {
    build();
    var row = rowOf(pill);
    state.sizes = oosSizesIn(row);
    /* a fully sold-out product opened from the CTA has no clicked pill —
       fall back to whatever the button remembered, else the first OOS size */
    var want = pill.getAttribute('data-size') || pill.getAttribute('data-bis-size');
    var hit = state.sizes.filter(function (s) { return s.size === want; })[0] || state.sizes[0];
    if (!hit) return false;
    state.size = hit.size; state.variantId = hit.variantId;

    var t = productTitle(pill);
    el.querySelector('.dzbis__sub').textContent =
      'Select your size and drop your email — we’ll hit you the second it’s back.';
    if (t) el.querySelector('.dzbis__h').setAttribute('data-product', t);

    renderSizes();
    msg('', '');
    el.querySelector('.dzbis__form').hidden = false;
    lastFocus = document.activeElement;
    el.removeAttribute('hidden');
    document.documentElement.classList.add('dzbis-open');
    var first = el.querySelector('.dzbis__size.is-on') || el.querySelector('#dzbis-email');
    if (first) first.focus();
    return true;
  }

  /* ---- CARD PATH -------------------------------------------------------
     Grid cards (homepage rails, collection grid — anywhere dzc-product-card
     renders) show "Sold out in {size} · Notify me". drawlz-size.js makes that
     line a button carrying data-handle + data-size.

     The PDP path above cannot serve these: a card is not inside a
     .drawlz-size__row, so rowOf() falls back to `document` and would collect
     every out-of-stock pill on the page, and productTitle() would read the
     collection's <h1> instead of the product.

     So the card hands us a handle and we ask Shopify for that ONE product.
     /products/{handle}.js returns options + variants — everything needed to
     build the same size list the PDP builds — which keeps cards free of the
     variant map that would otherwise have to be printed onto every tile in
     every grid. Cached per handle so reopening is instant. */
  var cardCache = {};

  function sizeIndexOf(p) {
    var opts = p.options || [];
    for (var i = 0; i < opts.length; i++) {
      var n = String(opts[i] && opts[i].name ? opts[i].name : opts[i]).toLowerCase();
      if (n.indexOf('size') !== -1) return i;
    }
    return 0;
  }

  function openFromCard(trigger) {
    var handle = trigger.getAttribute('data-handle');
    var want = trigger.getAttribute('data-size');
    if (!handle) return;
    build();
    if (cardCache[handle]) { showCard(cardCache[handle], want); return; }
    fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) return;
        cardCache[handle] = p;
        showCard(p, want);
      })
      .catch(function () { /* silent — the line stays, nothing opens */ });
  }

  function showCard(p, want) {
    var si = sizeIndexOf(p);
    /* DEDUPE BY SIZE. On a Colour x Size product every colour has its own
       variant per size, so a fully sold-out 3-colour hoodie yields XS/S/M/L/XL
       three times each — 15 buttons for 5 sizes. Verified against
       kids-drawlz-white-logo-hoodie. Keep the first variant for each size:
       the alert is "tell me when this size returns", and Klaviyo needs one
       variant to hang it on. */
    var seen = {};
    state.sizes = (p.variants || [])
      .filter(function (v) { return !v.available; })
      .map(function (v) {
        return {
          size: String((v.options || [])[si] || v.title),
          variantId: String(v.id)
        };
      })
      .filter(function (o) {
        if (!o.size || seen[o.size]) return false;
        seen[o.size] = 1;
        return true;
      });
    if (!state.sizes.length) return;

    var hit = state.sizes.filter(function (s) { return s.size === want; })[0] || state.sizes[0];
    state.size = hit.size;
    state.variantId = hit.variantId;

    el.querySelector('.dzbis__sub').textContent =
      'Select your size and drop your email — we’ll hit you the second it’s back.';
    el.querySelector('.dzbis__h').setAttribute('data-product', p.title || '');

    renderSizes();
    msg('', '');
    el.querySelector('.dzbis__form').hidden = false;
    lastFocus = document.activeElement;
    el.removeAttribute('hidden');
    document.documentElement.classList.add('dzbis-open');
    var first = el.querySelector('.dzbis__size.is-on') || el.querySelector('#dzbis-email');
    if (first) first.focus();
  }

  function close() {
    if (!el || el.hasAttribute('hidden')) return;
    el.setAttribute('hidden', '');
    document.documentElement.classList.remove('dzbis-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function msg(text, kind) {
    var m = el.querySelector('.dzbis__msg');
    m.textContent = text;
    m.className = 'dzbis__msg' + (kind ? ' is-' + kind : '');
  }

  /* ---- Klaviyo submit -------------------------------------------------- */
  function submit(e) {
    e.preventDefault();
    var email = el.querySelector('#dzbis-email').value.trim();
    var wantsMkt = el.querySelector('#dzbis-mkt').checked;
    var btn = el.querySelector('.dzbis__submit');

    if (!email || email.indexOf('@') < 1) { msg('Enter a valid email address.', 'err'); return; }
    if (!state.variantId) { msg('Couldn’t identify that size. Please reopen and try again.', 'err'); return; }
    if (!COMPANY) { msg('Alerts are unavailable right now. Please try again later.', 'err'); return; }

    btn.disabled = true; btn.textContent = 'Sending…'; msg('', '');

    subscribeBIS(email, state.variantId)
      .then(function () { return wantsMkt && LIST ? subscribeList(email) : null; })
      .then(function () {
        el.querySelector('.dzbis__form').hidden = true;
        msg('', '');
        var p = document.createElement('p');
        p.className = 'dzbis__done';
        p.textContent = 'You’re on the list — we’ll email you the moment your size is back.';
        el.querySelector('.dzbis__panel').appendChild(p);
        setTimeout(close, 2600);
      })
      .catch(function () {
        btn.disabled = false; btn.textContent = 'Notify Me';
        msg('That didn’t go through. Check the address and try again.', 'err');
      });
  }

  function subscribeBIS(email, variantId) {
    return fetch(BIS_URL + '?company_id=' + encodeURIComponent(COMPANY), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email: email } } }
          },
          relationships: {
            variant: {
              data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId }
            }
          }
        }
      })
    }).then(function (r) { if (!r.ok) throw new Error('bis ' + r.status); return r; });
  }

  /* Only runs when the box is ticked — an unticked box creates the restock
     alert and nothing else, which is the whole point of keeping them separate. */
  function subscribeList(email) {
    return fetch(SUB_URL + '?company_id=' + encodeURIComponent(COMPANY), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: LIST } } }
        }
      })
    }).catch(function () { /* marketing opt-in is best-effort; the alert already landed */ });
  }

  /* ---- wiring ---------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    /* FIRST, and with stopPropagation: the whole card is a link to the
       product, so without this the tap navigates instead of opening. */
    var cardBtn = e.target.closest('[data-drawlz-bis-card]');
    if (cardBtn) {
      e.preventDefault();
      e.stopPropagation();
      openFromCard(cardBtn);
      return;
    }
    var pill = e.target.closest('[data-drawlz-size]');
    if (pill) {
      if (isOOS(pill)) {
        e.preventDefault();
        e.stopPropagation();
        Array.prototype.forEach.call(
          rowOf(pill).querySelectorAll('[data-drawlz-size]'),
          function (p) { p.setAttribute('aria-pressed', String(p === pill)); }
        );
        toNotify(pill.getAttribute('data-size'), variantIdFor(pill));
      } else {
        fromNotify();
      }
      return;
    }
    var notify = e.target.closest('[data-drawlz-notify], .drawlz-cta.is-notify');
    if (notify) {
      e.preventDefault();
      /* prefer the pill the shopper actually chose; otherwise any OOS pill on
         the page (fully sold-out product opened straight from the CTA) */
      var src = document.querySelector('[data-drawlz-size].is-sold-out[aria-pressed="true"]') ||
                document.querySelector('[data-drawlz-size].is-sold-out') || notify;
      if (!open(src)) {
        msg('', '');
      }
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (!el || el.hasAttribute('hidden')) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    var f = el.querySelectorAll('button, input, [href], select, textarea');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  function boot() {
    COMPANY = klaviyoCompanyId();
    LIST = klaviyoListId();
    /* a product sold out at the server renders the notify CTA from Liquid —
       give it a size to work with so the drawer opens pre-populated */
    document.querySelectorAll('[data-drawlz-notify]').forEach(function (b) {
      if (!b.getAttribute('data-label-notify')) b.setAttribute('data-label-notify', b.textContent.trim());
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.DrawlzBIS = { open: open, close: close, company: function () { return COMPANY; } };
})();